import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ReviewStatusDotState } from '../../utils/reviewStatusDot';
import {
  ALL_REVIEW_STATUS_DOT_STATES,
  REVIEW_STATUS_DOT_FILTER_OPTIONS,
} from './reviewStatusFilterOptions';
import styles from './ReviewStatusFilterDropdown.module.css';

export interface ReviewStatusFilterDropdownProps {
  selected: Set<ReviewStatusDotState>;
  onChange: (selected: Set<ReviewStatusDotState>) => void;
  variant?: 'default' | 'column';
}

const dotClassForState = (state: ReviewStatusDotState): string => {
  switch (state) {
    case 'red':
      return styles.statusDotRed;
    case 'green':
      return styles.statusDotGreen;
    case 'pink':
      return styles.statusDotPink;
    case 'grey':
      return styles.statusDotGrey;
    default:
      return '';
  }
};

const isMultiSelectModifier = (event: React.MouseEvent): boolean =>
  event.ctrlKey || event.metaKey;

const ReviewStatusFilterDropdown: React.FC<ReviewStatusFilterDropdownProps> = ({
  selected,
  onChange,
  variant = 'default',
}) => {
  const [open, setOpen] = useState(false);
  const [panelPos, setPanelPos] = useState<{ top: number; left: number } | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const isColumn = variant === 'column';

  const allSelected = ALL_REVIEW_STATUS_DOT_STATES.every((state) => selected.has(state));
  const selectedDots = ALL_REVIEW_STATUS_DOT_STATES.filter((state) => selected.has(state));
  const hasActiveFilter =
    selectedDots.length > 0 && selectedDots.length < ALL_REVIEW_STATUS_DOT_STATES.length;
  const showStatusWord = isColumn || !hasActiveFilter || selectedDots.length === 1;

  useLayoutEffect(() => {
    if (!open || !isColumn) {
      setPanelPos(null);
      return undefined;
    }

    const updatePosition = () => {
      const rect = wrapRef.current?.getBoundingClientRect();
      if (!rect) return;
      const estimatedWidth = 56;
      const left = Math.max(
        8,
        Math.min(rect.left, window.innerWidth - estimatedWidth - 8)
      );
      setPanelPos({ top: rect.bottom + 6, left });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, isColumn]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (wrapRef.current?.contains(target) || panelRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  const toggleStatusInSelection = (state: ReviewStatusDotState) => {
    const next = new Set(selectedRef.current);
    if (next.has(state)) {
      next.delete(state);
    } else {
      next.add(state);
    }
    onChange(next);
  };

  const handleAllClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (isMultiSelectModifier(event)) {
      onChange(new Set(ALL_REVIEW_STATUS_DOT_STATES));
      return;
    }

    onChange(new Set());
    setOpen(false);
  };

  const handleStatusClick = (event: React.MouseEvent, state: ReviewStatusDotState) => {
    event.preventDefault();
    event.stopPropagation();

    if (isMultiSelectModifier(event)) {
      toggleStatusInSelection(state);
      return;
    }

    onChange(new Set([state]));
    setOpen(false);
  };

  const panel = open && (
    <div
      ref={panelRef}
      className={`${styles.panel} ${isColumn ? `${styles.panelFixed} ${styles.panelDotsOnly}` : ''}`.trim()}
      role="listbox"
      aria-multiselectable="true"
      aria-label="סטטוס"
      style={
        isColumn && panelPos
          ? { top: panelPos.top, left: panelPos.left, right: 'auto' }
          : undefined
      }
    >
      {isColumn ? (
        <>
          <div
            className={styles.dotOption}
            role="option"
            aria-selected={selected.size === 0 || allSelected}
            aria-label="הכל"
            onClick={handleAllClick}
          >
            <input
              type="checkbox"
              className={styles.dotOptionCheck}
              checked={selected.size === 0 || allSelected}
              onChange={() => undefined}
              tabIndex={-1}
            />
            <span className={styles.dotOptionAllLabel}>הכל</span>
          </div>
          {REVIEW_STATUS_DOT_FILTER_OPTIONS.map((option) => {
            const isSelected = selected.has(option.value);
            return (
              <div
                key={option.value}
                className={styles.dotOption}
                role="option"
                aria-selected={isSelected}
                aria-label={option.label}
                onClick={(event) => handleStatusClick(event, option.value)}
              >
                <input
                  type="checkbox"
                  className={styles.dotOptionCheck}
                  checked={isSelected}
                  onChange={() => undefined}
                  tabIndex={-1}
                />
                <span
                  className={`${styles.statusDot} ${dotClassForState(option.value)}`.trim()}
                />
              </div>
            );
          })}
        </>
      ) : (
        <>
          <div className={styles.panelTitle}>סטטוס</div>

          <div
            className={styles.optionRow}
            role="option"
            aria-selected={allSelected}
            onClick={handleAllClick}
          >
            <span className={styles.optionDotSlot} aria-hidden />
            <span className={styles.optionLabel}>הכל</span>
            <input
              type="checkbox"
              className={styles.optionCheck}
              checked={allSelected}
              onChange={() => undefined}
              tabIndex={-1}
            />
          </div>

          {REVIEW_STATUS_DOT_FILTER_OPTIONS.map((option) => (
            <div
              key={option.value}
              className={styles.optionRow}
              role="option"
              aria-selected={selected.has(option.value)}
              onClick={(event) => handleStatusClick(event, option.value)}
            >
              <span className={styles.optionDotSlot}>
                <span
                  className={`${styles.statusDot} ${dotClassForState(option.value)}`.trim()}
                  aria-hidden
                />
              </span>
              <span className={styles.optionLabel}>{option.label}</span>
              <input
                type="checkbox"
                className={styles.optionCheck}
                checked={selected.has(option.value)}
                onChange={() => undefined}
                tabIndex={-1}
              />
            </div>
          ))}
        </>
      )}
    </div>
  );

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <button
        type="button"
        className={`${styles.trigger} ${isColumn ? styles.triggerColumn : ''} ${open ? styles.triggerOpen : ''} ${hasActiveFilter && isColumn ? styles.triggerFiltered : ''}`.trim()}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((prev) => !prev);
        }}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label="סטטוס"
      >
        {isColumn ? (
          hasActiveFilter ? (
            <>
              <span className={styles.triggerArrow} aria-hidden>
                ▾
              </span>
              <span className={styles.triggerDotsGrid} aria-hidden>
                {selectedDots.map((state) => (
                  <span
                    key={state}
                    className={`${styles.statusDot} ${dotClassForState(state)}`.trim()}
                  />
                ))}
              </span>
            </>
          ) : (
            <span className={styles.triggerLabel}>סטטוס</span>
          )
        ) : (
          <>
            {hasActiveFilter && (
              <span className={styles.triggerDots} aria-hidden>
                {selectedDots.map((state) => (
                  <span
                    key={state}
                    className={`${styles.statusDot} ${dotClassForState(state)}`.trim()}
                  />
                ))}
              </span>
            )}
            {showStatusWord && <span className={styles.triggerLabel}>סטטוס</span>}
            <span className={styles.triggerArrow} aria-hidden>
              ▾
            </span>
          </>
        )}
      </button>

      {isColumn
        ? panel && createPortal(panel, document.body)
        : panel}
    </div>
  );
};

export default ReviewStatusFilterDropdown;
